import React, { useState, useEffect } from "react";
import { CustomerService } from "services/CustomerService";
import { toast } from "react-toastify";
import ApiKeySelect from "./common/ApiKeySelect";

interface SendSmsSidebarProps {
  customerId: string | number;
  // Key picked in the page's API Key dropdown, used as the starting choice.
  defaultApiKeyId?: string;
  onClose: () => void;
  onSend: () => void;
}

const SendSmsSidebar: React.FC<SendSmsSidebarProps> = ({ customerId, defaultApiKeyId = "", onClose, onSend }) => {
  const [apiKeyId, setApiKeyId] = useState(defaultApiKeyId);
  const [apiKey, setApiKey] = useState<string>("");
  const [recipientNumber, setRecipientNumber] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sending authenticates with the chosen key's value.
  useEffect(() => {
    setApiKey("");
    if (!apiKeyId) return;
    let cancelled = false;
    CustomerService.apiKey
      .getApiKeyById(parseInt(apiKeyId))
      .then((res) => {
        if (!cancelled && res.data?.key) setApiKey(res.data.key);
      })
      .catch((err) => console.error("Failed to fetch API key:", err));
    return () => {
      cancelled = true;
    };
  }, [apiKeyId]);

  const handleRecipientNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^[\d-]*$/.test(value)) {
      setRecipientNumber(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await CustomerService.sms.sendSms(
        { To: [recipientNumber], Message: message },
        apiKey,
      );
      toast.success("SMS sent successfully!", { position: "top-right", autoClose: 3000 });
      onSend();
      onClose();
      setRecipientNumber("");
      setMessage("");
    } catch (err: any) {
      toast.error(err.response?.data || "Failed to send SMS", { position: "top-right", autoClose: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <ApiKeySelect
        id="send-sms-api-key"
        stacked
        label="Send from API key *"
        emptyLabel="Choose an API key"
        customerId={customerId}
        value={apiKeyId}
        onChange={setApiKeyId}
      />
      <div className="mb-3">
        <label htmlFor="recipientNumber" className="form-label fw-bold">Recipient Number *</label>
        <input
          type="tel"
          className="form-control"
          id="recipientNumber"
          value={recipientNumber}
          onChange={handleRecipientNumberChange}
          placeholder="213-315-0711"
          required
          maxLength={12}
        />
        <div className="form-text">Enter the recipient's phone number in XXX-XXX-XXXX format</div>
      </div>
      <div className="mb-4">
        <label htmlFor="message" className="form-label fw-bold">Message *</label>
        <textarea
          className="form-control"
          id="message"
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter your message..."
          required
        />
        <div className="form-text">Maximum 160 characters per message</div>
      </div>
      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting || !apiKey}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Sending...
            </>
          ) : "Send SMS"}
        </button>
      </div>
    </form>
  );
};

export default SendSmsSidebar;
